import { useLayoutEffect } from 'react';
import type { DependencyList, RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/*
 * Editorial motion system — declarative data attributes, scoped per section.
 *  data-m-head            heading reveal (masked lines if it contains [data-m-line])
 *  data-m-fade            small label / caption fade
 *  data-m-in="left|right|up"  directional entrance (data-m-dist, data-m-at, data-m-dur, data-m-ease, data-m-mask="left")
 *  data-m-zoom            inner image scale 1.04 → 1 during parent entrance
 *  data-m-group           coordinates [data-m-in] children into one timeline (tablet/desktop)
 *  data-m-par="px"        scroll-scrubbed vertical travel (desktop), data-m-par-t for tablet
 *  data-m-cover           parallax layer is scaled just enough to never expose its frame
 *  data-m-par-scope       shared trigger for all parallax layers inside it
 * Hidden start states are only applied inside the GSAP context, so content stays visible without JS.
 */

let globalRefreshBound = false;
function bindGlobalRefresh() {
  if (globalRefreshBound || typeof window === 'undefined') return;
  globalRefreshBound = true;
  const refresh = () => ScrollTrigger.refresh();
  if (document.readyState !== 'complete') window.addEventListener('load', refresh, { once: true });
  document.fonts?.ready.then(refresh).catch(() => undefined);
}

function startState(el: HTMLElement, k: number): gsap.TweenVars {
  const dir = el.dataset.mIn;
  const d = Number(el.dataset.mDist || 65) * k;
  const s: gsap.TweenVars = { opacity: 0 };
  if (dir === 'left') s.x = -d;
  else if (dir === 'right') s.x = d;
  else s.y = d;
  if (el.dataset.mMask === 'left') s.clipPath = 'inset(0% 100% 0% 0%)';
  return s;
}

function addItem(tl: gsap.core.Timeline, el: HTMLElement, k: number, at: number) {
  const dur = Number(el.dataset.mDur || 0.85);
  const ease = el.dataset.mEase || 'power3.out';
  const to: gsap.TweenVars = { x: 0, y: 0, opacity: 1, duration: dur, ease, clearProps: 'transform,opacity,clipPath' };
  if (el.dataset.mMask) to.clipPath = 'inset(0% 0% 0% 0%)';
  tl.fromTo(el, startState(el, k), to, at);
  const zoom = el.querySelector<HTMLElement>('[data-m-zoom]');
  if (zoom) tl.fromTo(zoom, { scale: 1.04 }, { scale: 1, duration: dur + 0.1, ease, clearProps: 'transform' }, at);
}

export default function useEditorialMotion(scope: RefObject<HTMLElement | null>, deps: DependencyList = []) {
  useLayoutEffect(() => {
    const root = scope.current;
    if (!root) return undefined;
    bindGlobalRefresh();
    let ctx: gsap.Context | undefined;

    try {
      ctx = gsap.context(() => {
        const mm = gsap.matchMedia();
        mm.add(
          {
            desk: '(min-width: 1024px)',
            tab: '(min-width: 768px) and (max-width: 1023px)',
            mob: '(max-width: 767px)',
            reduce: '(prefers-reduced-motion: reduce)',
          },
          (c) => {
            const { desk, tab, mob, reduce } = c.conditions as Record<string, boolean>;
            if (reduce) return;
            const k = desk ? 1 : tab ? 0.75 : 0.5;
            const q = (s: string) => Array.from(root.querySelectorAll<HTMLElement>(s));

            // Headings
            q('[data-m-head]').forEach((h) => {
              const lines = q('[data-m-line]').filter((l) => h.contains(l));
              const st = { trigger: h, start: 'top 88%', once: true };
              if (lines.length) {
                gsap.fromTo(lines, { y: 24 * k, opacity: 0 }, {
                  y: 0, opacity: 1, duration: 0.75, ease: 'power3.out', stagger: 0.12, clearProps: 'transform,opacity', scrollTrigger: st,
                });
              } else {
                gsap.fromTo(h, { y: 22 * k, opacity: 0, clipPath: 'inset(0% 0% 100% 0%)' }, {
                  y: 0, opacity: 1, clipPath: 'inset(-20% -5% -30% -5%)', duration: 0.75, ease: 'power3.out', clearProps: 'transform,opacity,clipPath', scrollTrigger: st,
                });
              }
            });

            // Labels / captions
            q('[data-m-fade]').forEach((el) => {
              gsap.fromTo(el, { opacity: 0, y: 10 * k }, {
                opacity: 1, y: 0, duration: 0.6, delay: Number(el.dataset.mAt || 0), ease: 'power2.out', clearProps: 'transform,opacity',
                scrollTrigger: { trigger: el, start: 'top 92%', once: true },
              });
            });

            // Directional entrances
            const handled = new Set<HTMLElement>();
            (mob ? [] : q('[data-m-group]')).forEach((g) => {
              const items = q('[data-m-in]').filter((el) => g.contains(el));
              if (!items.length) return;
              const tl = gsap.timeline({ scrollTrigger: { trigger: g, start: 'top 80%', once: true } });
              items.forEach((el, i) => {
                handled.add(el);
                addItem(tl, el, k, Number(el.dataset.mAt ?? i * 0.12));
              });
            });
            q('[data-m-in]').filter((el) => !handled.has(el)).forEach((el) => {
              const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
              addItem(tl, el, k, mob ? 0 : Number(el.dataset.mAt || 0));
            });

            // Scroll-linked parallax (one document scroll source, scrubbed)
            if (desk || tab) {
              q('[data-m-par]').forEach((el) => {
                const base = Number(el.dataset.mPar || 0);
                const d = desk ? base : Number(el.dataset.mParT ?? base * 0.4);
                if (!d) return;
                const trigger = (el.closest('[data-m-par-scope]') as HTMLElement | null) ?? el.parentElement ?? el;
                if (el.hasAttribute('data-m-cover')) {
                  gsap.set(el, { scale: () => 1 + d / Math.max(el.offsetHeight, 1) + 0.02 });
                }
                gsap.fromTo(el, { y: d / 2 }, {
                  y: -d / 2,
                  ease: 'none',
                  scrollTrigger: { trigger, start: 'top bottom', end: 'bottom top', scrub: Number(el.dataset.mScrub || 0.9), invalidateOnRefresh: true },
                });
              });
            }
          },
        );
      }, root);
    } catch {
      // Never leave content hidden if setup fails
      ctx?.revert();
      ctx = undefined;
    }

    return () => ctx?.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, ...deps]);
}